type ErrorLike = {
  name?: string
  message?: string
  stack?: string
}

const safeRead = (error: ErrorLike, key: keyof ErrorLike): string => {
  try {
    return error[key] ?? ""
  } catch {
    return ""
  }
}

// Plain-string messages webpack/Next emit when a chunk cannot be downloaded or
// resolved. These are almost always caused by the browser running against a
// stale build right after a deploy.
const CHUNK_LOAD_MESSAGE_PATTERNS = [
  "Loading chunk",
  "Loading CSS chunk",
  "error loading dynamically imported module",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
]

/**
 * Detects errors that indicate the browser is running against a stale build
 * (missing/mismatched webpack chunks) — the kind a single reload usually fixes.
 *
 * Besides the explicit `ChunkLoadError`, this also covers the webpack runtime's
 *   `TypeError: Cannot read properties of undefined (reading 'call')`
 * thrown from `__webpack_require__` when a module-factory id is missing, and an
 * imported binding that resolved to `undefined` (`(0, n.Q5) is not a function`).
 * Both are only treated as recoverable when the stack points at build output,
 * so genuine application bugs are not silently swallowed by a reload.
 */
export const isRecoverableChunkError = (error?: ErrorLike | null): boolean => {
  if (!error) return false

  if (safeRead(error, "name") === "ChunkLoadError") return true

  const message = safeRead(error, "message")
  if (
    CHUNK_LOAD_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern))
  ) {
    return true
  }

  // The same webpack module-factory failure is phrased differently per engine:
  //   Chrome/V8:    Cannot read properties of undefined (reading 'call')
  //   older V8:     Cannot read property 'call' of undefined
  //   Safari/WebKit: undefined is not an object (evaluating 'b[e].call')
  const mentionsUndefinedCall =
    message.includes("reading 'call'") ||
    message.includes("'call' of undefined") ||
    message.includes(".call')")
  if (mentionsUndefinedCall) {
    return safeRead(error, "stack").includes("webpack")
  }

  // The bundler compiles a call to an imported function as `(0, module.export)(...)`,
  // so when a re-deployed chunk no longer exports that (re-mangled) name the
  // engine throws `(0 , n.Q5) is not a function`.
  const mentionsImportedBindingCall =
    /\(0\s*,\s*[\w$]+\.[\w$]+\)\s+is not a function/.test(message)
  if (mentionsImportedBindingCall) {
    const stack = safeRead(error, "stack")
    return stack.includes("_next/static") || stack.includes("webpack")
  }

  return false
}
